import ky from 'ky';

function getRuntimeBase() {
  if (typeof window !== 'undefined' && window.location) {
    const { origin } = window.location;
    // Assume same-origin (use dev proxy for API/PDF during local dev)
    return origin;
  }
  // SSR / Node fallback
  return 'http://localhost:4000';
}

const envBase =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) ||
  (typeof process !== 'undefined' && process.env && process.env.VITE_API_BASE) ||
  (typeof process !== 'undefined' && process.env && process.env.API_BASE_URL) ||
  '';

const API_BASE = (() => {
  const runtimeBase = getRuntimeBase();
  const runtimeBaseTrimmed = runtimeBase.replace(/\/+$/, '');
  const trimmedEnv = String(envBase || '').trim();
  if (!trimmedEnv) {
    return runtimeBaseTrimmed;
  }

  const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV;
  if (isDev && typeof window !== 'undefined' && window.location?.origin) {
    try {
      const envOrigin = new URL(trimmedEnv, window.location.origin).origin;
      if (envOrigin !== window.location.origin) {
        // Prefer same-origin in dev so the Vite proxy can handle cross-origin APIs.
        return runtimeBaseTrimmed;
      }
    } catch {
      // ignore invalid env URL
    }
  }

  return trimmedEnv.replace(/\/+$/, '');
})();

if (typeof console !== 'undefined' && import.meta?.env?.DEV) {
  console.info('[api] base URL:', API_BASE || '(none)');
}

export const http = ky.create({
  prefixUrl: API_BASE || undefined,
  timeout: 90_000,
  retry: {
    limit: 1,
    methods: ['get', 'post', 'put', 'delete'],
  },
  hooks: {
    beforeRequest: [
      (request) => {
        request.headers.set('x-claimsupplement-client', 'web');
      },
    ],
  },
  throwHttpErrors: false,
});

export async function requestJson(path, { schema, searchParams, ...options } = {}) {
  const response = await http(path, { searchParams, ...options });
  const payload = await parseResponsePayload(response);
  if (!response.ok) {
    const message = payload?.error || response.statusText || 'Request failed';
    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  if (!schema) return payload;

  const result = schema.safeParse(payload);
  if (!result.success) {
    if (typeof console !== 'undefined') {
      console.groupCollapsed?.('[api] schema mismatch', path);
      console.warn?.('issues:', result.error.issues);
      console.warn?.('payload:', payload);
      console.groupEnd?.();
    }
    const error = new Error(`API response validation failed for ${path}`);
    error.status = response.status;
    error.payload = payload;
    error.issues = result.error.issues;
    throw error;
  }
  return result.data;
}

async function parseResponsePayload(response) {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

export function getApiBase() {
  return API_BASE;
}
